// Rule-set claims — FMEA workbook ingest + case-learned proposals (construct.R8).
// See docs/engines/rule-set.md.

import type { Claim, AuthorityRule, ResolvedVersion, Ruling } from '@/versions/v1/schema/claim';
import type { DiagnosticRule } from '@/versions/v1/schema/diagnostic-rule';
import { DEFAULT_CURSOR } from '@/versions/v1/schema/reading-series';
import { isoDateFromRaisedAt } from '@/versions/v1/shared/clock.derive';
import {
  ADVISORY_DEPTH_PATTERN_RULES,
  MET_DIAGNOSTICS_SECTION_RULES,
} from '@/versions/v1/shared/bunker-hill-met-diagnostics-rules';
import { DIAGNOSTIC_RULES } from '@/versions/v1/shared/bunker-hill-seed';
import {
  ADVISORY_RULES,
  CASE_OUTCOMES,
  CASES,
  FMEA_UPDATE_REVIEWS,
} from '@/versions/v1/shared/bunker-hill-advisory-seed';

export const FMEA_SOURCE_ID = 'src-fmea';

const WORKBOOK_RULE_IDS = new Set(DIAGNOSTIC_RULES.map((row) => row.id));

function earliestWorkbookRuleVintage(cursor: string): { date: string; provenance: string } {
  const candidates: { date: string; provenance: string; ms: number }[] = [];

  for (const review of FMEA_UPDATE_REVIEWS) {
    const ruleId = review.proposedChange.ruleId;
    if (!ruleId || !WORKBOOK_RULE_IDS.has(ruleId)) continue;
    if (review.reviewedAt) {
      const date = isoDateFromRaisedAt(review.reviewedAt, cursor);
      candidates.push({
        date,
        provenance: `vintage = earliest seeded use of a workbook rule (FMR review ${review.id} reviewedAt) [syn]`,
        ms: Date.parse(date),
      });
    }
    if (review.approvedAt) {
      const date = isoDateFromRaisedAt(review.approvedAt, cursor);
      candidates.push({
        date,
        provenance: `vintage = earliest seeded use of a workbook rule (FMR approval ${review.id}) [syn]`,
        ms: Date.parse(date),
      });
    }
  }

  for (const outcome of CASE_OUTCOMES) {
    if (!outcome.proposedRuleId || !WORKBOOK_RULE_IDS.has(outcome.proposedRuleId)) continue;
    const caseItem = CASES.find((row) => row.id === outcome.caseId);
    if (caseItem?.raisedAt) {
      const date = isoDateFromRaisedAt(caseItem.raisedAt, cursor);
      candidates.push({
        date,
        provenance: `vintage = earliest seeded use of a workbook rule (${caseItem.id} raisedAt) [syn]`,
        ms: Date.parse(date),
      });
    }
  }

  if (candidates.length === 0) {
    return {
      date: isoDateFromRaisedAt('06 Jun', cursor),
      provenance: 'vintage = earliest seeded use of a workbook rule (fallback) [syn]',
    };
  }
  candidates.sort((a, b) => a.ms - b.ms);
  return { date: candidates[0]!.date, provenance: candidates[0]!.provenance };
}

const workbookVintage = earliestWorkbookRuleVintage(DEFAULT_CURSOR);
export const FMEA_VINTAGE = workbookVintage.date;
const FMEA_VINTAGE_PROVENANCE = workbookVintage.provenance;

export const RULE_AUTHORITY: readonly AuthorityRule[] = [
  {
    kind: 'rule',
    // paul-fmea.A7 (#274): `paper` ranks below `fmea` — a literature source
    // can propose a rule claim but never outranks the workbook.
    ranking: ['fmea', 'control-plan', 'tarp', 'mass-balance', 'paper', 'dcs-fd', 'flowsheet'],
    note:
      'FMEA workbook wins for Met Diagnostics rule claims; control plan and TARP rank per docs/logic/ingestion/setup.md § Source kinds; a paper ranks below them and only ever yields a proposed claim.',
  },
];

function ruleById(ruleId: string): DiagnosticRule | undefined {
  return (
    DIAGNOSTIC_RULES.find((row) => row.id === ruleId) ??
    ADVISORY_RULES.find((row) => row.id === ruleId) ??
    MET_DIAGNOSTICS_SECTION_RULES.find((row) => row.id === ruleId)
  );
}

function documentClaim(rule: DiagnosticRule): Claim<DiagnosticRule> {
  return {
    id: `claim-rule-${rule.id}-doc`,
    kind: 'rule',
    subject: { kind: 'rule', id: rule.id },
    value: rule,
    valueLabel: rule.diagnosis,
    source: { tier: 'document', sourceId: FMEA_SOURCE_ID, sourceKind: 'fmea' },
    vintage: FMEA_VINTAGE,
    status: 'accepted',
    provenance: `FMEA workbook · Met Diagnostics table · ${FMEA_VINTAGE_PROVENANCE}`,
  };
}

function caseClaim(
  rule: DiagnosticRule,
  caseId: string,
  at: string,
): Claim<DiagnosticRule> {
  const hasDocClaim = DIAGNOSTIC_RULES.some((row) => row.id === rule.id);
  return {
    id: `claim-rule-${rule.id}-case`,
    kind: 'rule',
    subject: { kind: 'rule', id: rule.id },
    value: rule,
    valueLabel: rule.diagnosis,
    source: { tier: 'case', caseId, at },
    vintage: at,
    status: 'disputed',
    ...(hasDocClaim ? { conflictsWith: [`claim-rule-${rule.id}-doc`] } : {}),
    provenance: `Case ${caseId} · ${at}`,
  };
}

const workbookClaims = DIAGNOSTIC_RULES.map(documentClaim);

/** V8 register section batches — document-tier, accepted, dated between FMEA vintage and cursor. */
function registerDocumentClaim(rule: DiagnosticRule, acceptedAt: string): Claim<DiagnosticRule> {
  return {
    id: `claim-rule-${rule.id}-register`,
    kind: 'rule',
    subject: { kind: 'rule', id: rule.id },
    value: rule,
    valueLabel: rule.diagnosis,
    source: { tier: 'document', sourceId: FMEA_SOURCE_ID, sourceKind: 'fmea' },
    vintage: acceptedAt,
    status: 'accepted',
    provenance: `FMEA V8 Met Diagnostics register · structured ${acceptedAt}`,
  };
}

const PROSE_STRUCTURED_IDS = [
  'dr-ag-3418a',
  'dr-cuso4',
  'dr-air-valve',
  'dr-p80-apex',
] as const;

const proseStructuredClaims: Claim<DiagnosticRule>[] = PROSE_STRUCTURED_IDS.flatMap((ruleId) => {
  const rule = ADVISORY_RULES.find((row) => row.id === ruleId);
  if (!rule?.when?.length) return [];
  const acceptedAt =
    ruleId === 'dr-p80-apex' ? '08 Jun 09:00' : ruleId === 'dr-air-valve' ? '07 Jun 12:00' : '07 Jun 12:00';
  return [registerDocumentClaim(rule, isoDateFromRaisedAt(acceptedAt, DEFAULT_CURSOR))];
});

const SECTION_BATCH_1 = MET_DIAGNOSTICS_SECTION_RULES.slice(0, 10);
const SECTION_BATCH_2 = MET_DIAGNOSTICS_SECTION_RULES.slice(10, 17);
const SECTION_BATCH_3 = MET_DIAGNOSTICS_SECTION_RULES.slice(17, 23);
const SECTION_BATCH_4 = MET_DIAGNOSTICS_SECTION_RULES.slice(23);

const registerBatch1Claims = SECTION_BATCH_1.map((rule) =>
  registerDocumentClaim(rule, isoDateFromRaisedAt('07 Jun 12:00', DEFAULT_CURSOR)),
);
const registerBatch2Claims = SECTION_BATCH_2.map((rule) =>
  registerDocumentClaim(rule, isoDateFromRaisedAt('07 Jun 16:00', DEFAULT_CURSOR)),
);
const registerBatch3Claims = SECTION_BATCH_3.map((rule) =>
  registerDocumentClaim(rule, isoDateFromRaisedAt('08 Jun 06:00', DEFAULT_CURSOR)),
);
const registerBatch4Claims = SECTION_BATCH_4.map((rule) =>
  registerDocumentClaim(rule, isoDateFromRaisedAt('08 Jun 09:00', DEFAULT_CURSOR)),
);

const drAirFlowZn = ADVISORY_RULES.find((row) => row.id === 'dr-air-flow-zn');
const registerBatch5Claims = [
  ...ADVISORY_DEPTH_PATTERN_RULES.map((rule) =>
    registerDocumentClaim(rule, isoDateFromRaisedAt('08 Jun 10:00', DEFAULT_CURSOR)),
  ),
  ...(drAirFlowZn?.when?.length
    ? [registerDocumentClaim(drAirFlowZn, isoDateFromRaisedAt('08 Jun 10:00', DEFAULT_CURSOR))]
    : []),
];

const proposedRuleIds = [
  ...new Set(
    CASE_OUTCOMES.map((row) => row.proposedRuleId).filter((id): id is string => Boolean(id)),
  ),
];

const caseClaims: Claim<DiagnosticRule>[] = proposedRuleIds.flatMap((ruleId) => {
  const rule = ruleById(ruleId);
  const outcome = CASE_OUTCOMES.find((row) => row.proposedRuleId === ruleId);
  if (!rule || !outcome) return [];
  const caseItem = outcome.caseId;
  const at =
    FMEA_UPDATE_REVIEWS.find((row) => row.caseOutcomeId === outcome.id)?.reviewedAt ?? '06 Jun';
  return [caseClaim(rule, caseItem, at)];
});

/** Narrower dr-dart-valve after FMR-0006 — the approved row from ADVISORY_RULES. */
const dartValveApproved = ADVISORY_RULES.find((row) => row.id === 'dr-dart-valve');
const dartValveNarrowClaim: Claim<DiagnosticRule> | undefined = dartValveApproved
  ? {
      id: 'claim-rule-dr-dart-valve-narrow',
      kind: 'rule',
      subject: { kind: 'rule', id: 'dr-dart-valve' },
      value: dartValveApproved,
      valueLabel: dartValveApproved.diagnosis,
      source: { tier: 'ruled', rulingId: 'ruling-fmr-0006-approval' },
      vintage: '07 Jun 08:30',
      status: 'accepted',
      provenance: 'FMR-0006 · narrower-condition approved by Plant Manager',
    }
  : undefined;

const PAPER_SOURCE_ID = 'src-paper-sb-bi';

/**
 * paul-fmea.A7 (#274): built directly as a `document`-tier claim — not
 * through the generic case-claim pipeline above — so the `paper` SourceKind's
 * authority ranking is actually wired to a claim, and SourceDocument →
 * Claim traceability is direct via `sourceId`. `status: 'disputed'`, never
 * `accepted`: isIngestedRuleClaim() keys off this so the paper never counts
 * as "ingested into the model" until a Ruling promotes it — which does not
 * happen at seed (FMR-0017's approvalStatus stays 'pending').
 */
function paperClaim(): Claim<DiagnosticRule> | undefined {
  const rule = ruleById('dr-sb-bi-depression');
  if (!rule) return undefined;
  return {
    id: 'claim-rule-dr-sb-bi-depression-paper',
    kind: 'rule',
    subject: { kind: 'rule', id: rule.id },
    value: rule,
    valueLabel: rule.diagnosis,
    source: { tier: 'document', sourceId: PAPER_SOURCE_ID, sourceKind: 'paper' },
    vintage: '2022',
    status: 'disputed',
    provenance:
      'Smith, Bruckard & Sparrow (2022) · Mineral Processing and Extractive Metallurgy 131(3) · DOI 10.1080/25726641.2021.1967079 — literature review, not a plant sample',
  };
}

const paperRuleClaim = paperClaim();

export const RULE_CLAIMS: readonly Claim<DiagnosticRule>[] = [
  ...workbookClaims,
  ...proseStructuredClaims,
  ...registerBatch1Claims,
  ...registerBatch2Claims,
  ...registerBatch3Claims,
  ...registerBatch4Claims,
  ...registerBatch5Claims,
  ...caseClaims,
  ...(dartValveNarrowClaim ? [dartValveNarrowClaim] : []),
  ...(paperRuleClaim ? [paperRuleClaim] : []),
];

const KNOWN_CASE_IDS = new Set(CASES.map((row) => row.id));

/**
 * A rule claim counts as "ingested into the model" — the Plant model chip
 * and the Ingestion FMEA card — only if it came from the workbook document
 * itself, or from a case claim backed by a real `CASES` row.
 * The seven rules2 (#250) proposals are case claims sourced from synthetic
 * `case-paul-a5-*` ids with no `CASES` row (they are proposed straight from
 * Paul's cause lists, not learned from a live incident) and must not count
 * here — they are pending sign-off, not ingested (R2-F1).
 *
 * A `document`-tier claim also checks `status`: paul-fmea.A7 (#274) adds a
 * `paper`-sourced document claim seeded `disputed` (a proposal, not a live
 * rule) — without the status check it would inflate this count the moment
 * it exists. A no-op for every pre-existing document claim, which are all
 * seeded `accepted`.
 */
export function isIngestedRuleClaim(claim: Claim<DiagnosticRule>): boolean {
  if (claim.subject.kind === 'band') return false;
  if (claim.source.tier === 'document') return claim.status === 'accepted';
  if (claim.source.tier === 'case') return KNOWN_CASE_IDS.has(claim.source.caseId);
  return false;
}

export const SEEDED_RULINGS: readonly Ruling[] = FMEA_UPDATE_REVIEWS.flatMap((review) => {
  const ruleId = review.proposedChange.ruleId;
  if (!ruleId) return [];
  const caseClaimId = `claim-rule-${ruleId}-case`;
  // paul-fmea.A7 (#274): dr-sb-bi-depression has no case-tier claim at all
  // (its CASE_OUTCOMES row deliberately omits proposedRuleId) — the review
  // ruling must accept the paper's own document-tier claim instead.
  const reviewClaimId =
    ruleId === 'dr-sb-bi-depression' ? 'claim-rule-dr-sb-bi-depression-paper' : caseClaimId;
  const narrowClaimId = ruleId === 'dr-dart-valve' ? 'claim-rule-dr-dart-valve-narrow' : reviewClaimId;
  const rows: Ruling[] = [];
  if (review.reviewedBy && review.reviewStatus === 'approved') {
    rows.push({
      id: `ruling-${review.id}-review`,
      subject: { kind: 'rule', id: ruleId },
      acceptsClaimId: reviewClaimId,
      author: { role: 'metallurgist', name: review.reviewedBy },
      decidedAt: review.reviewedAt ?? '06 Jun',
      reason: review.reviewNotes ?? review.proposedChange.summary,
      stage: 'review',
    });
  }
  if (review.approvalStatus === 'approved' && review.approvedBy) {
    rows.push({
      id: `ruling-${review.id}-approval`,
      subject: { kind: 'rule', id: ruleId },
      acceptsClaimId: narrowClaimId,
      author: { role: 'gm', name: review.approvedBy },
      decidedAt: review.approvedAt ?? '07 Jun 08:30',
      reason: review.proposedChange.summary,
      stage: 'approval',
    });
  }
  return rows;
});

const V1_CLAIM_IDS = workbookClaims.map((row) => row.id);

const V2_CLAIM_IDS = [
  ...V1_CLAIM_IDS,
  ...(dartValveNarrowClaim ? [dartValveNarrowClaim.id] : []),
];

const V3_CLAIM_IDS = [
  ...V2_CLAIM_IDS,
  ...proseStructuredClaims.filter((row) => row.subject.id !== 'dr-p80-apex').map((row) => row.id),
  ...registerBatch1Claims.map((row) => row.id),
];

const V4_CLAIM_IDS = [...V3_CLAIM_IDS, ...registerBatch2Claims.map((row) => row.id)];

const V5_CLAIM_IDS = [...V4_CLAIM_IDS, ...registerBatch3Claims.map((row) => row.id)];

const V6_CLAIM_IDS = [
  ...V5_CLAIM_IDS,
  ...registerBatch4Claims.map((row) => row.id),
  ...proseStructuredClaims.filter((row) => row.subject.id === 'dr-p80-apex').map((row) => row.id),
];

const V7_CLAIM_IDS = [...V6_CLAIM_IDS, ...registerBatch5Claims.map((row) => row.id)];

export const RULE_SET_VERSIONS: readonly ResolvedVersion[] = [
  {
    id: 'rs-v1',
    scope: 'rule-set',
    n: 1,
    acceptedAt: FMEA_VINTAGE,
    acceptedBy: { role: 'gm', name: 'Plant Manager' },
    claimIds: V1_CLAIM_IDS,
    note: `FMEA workbook ingest — ${V1_CLAIM_IDS.length} Met Diagnostics structured rules`,
  },
  {
    id: 'rs-v2',
    scope: 'rule-set',
    n: 2,
    acceptedAt: isoDateFromRaisedAt('07 Jun 08:30', DEFAULT_CURSOR),
    acceptedBy: { role: 'gm', name: 'Plant Manager' },
    claimIds: V2_CLAIM_IDS,
    supersedes: 'rs-v1',
    note: 'FMR-0006 — dr-dart-valve narrower condition approved',
  },
  {
    id: 'rs-v3',
    scope: 'rule-set',
    n: 3,
    acceptedAt: isoDateFromRaisedAt('07 Jun 12:00', DEFAULT_CURSOR),
    acceptedBy: { role: 'gm', name: 'Plant Manager' },
    claimIds: V3_CLAIM_IDS,
    supersedes: 'rs-v2',
    note: 'V8 register — Pb recovery and Pb con grade sections structured',
  },
  {
    id: 'rs-v4',
    scope: 'rule-set',
    n: 4,
    acceptedAt: isoDateFromRaisedAt('07 Jun 16:00', DEFAULT_CURSOR),
    acceptedBy: { role: 'gm', name: 'Plant Manager' },
    claimIds: V4_CLAIM_IDS,
    supersedes: 'rs-v3',
    note: 'V8 register — Ag, Zn recovery and Zn con grade sections structured',
  },
  {
    id: 'rs-v5',
    scope: 'rule-set',
    n: 5,
    acceptedAt: isoDateFromRaisedAt('08 Jun 06:00', DEFAULT_CURSOR),
    acceptedBy: { role: 'gm', name: 'Plant Manager' },
    claimIds: V5_CLAIM_IDS,
    supersedes: 'rs-v4',
    note: 'V8 register — dual recovery decline and tailings sections structured',
  },
  {
    id: 'rs-v6',
    scope: 'rule-set',
    n: 6,
    acceptedAt: isoDateFromRaisedAt('08 Jun 09:00', DEFAULT_CURSOR),
    acceptedBy: { role: 'gm', name: 'Plant Manager' },
    claimIds: V6_CLAIM_IDS,
    supersedes: 'rs-v5',
    note: 'V8 register — grinding, flotation and penalty sections structured; cyclone wear rule live',
  },
  {
    id: 'rs-v7',
    scope: 'rule-set',
    n: 7,
    acceptedAt: isoDateFromRaisedAt('08 Jun 10:00', DEFAULT_CURSOR),
    acceptedBy: { role: 'gm', name: 'Plant Manager' },
    claimIds: V7_CLAIM_IDS,
    supersedes: 'rs-v6',
    note: 'advisory-depth — Zn pH low/high rules live for patterns (MD-021, MD-025)',
  },
];

export const FAILURE_MODE_RULE_IDS: Readonly<Record<string, string>> = {
  'fm-zn-ph': 'dr-zn-ph',
  'fm-3418a': 'dr-ag-3418a',
  'fm-apex': 'dr-p80-apex',
  'fm-as': 'dr-as-ore',
  'fm-gangue': 'dr-mineralogy',
  // rules2 (#250) — one fm id maps to one dr id (Record<string, string> is
  // 1:1); fm-air-flow and fm-froth-height are cause-level rows but their
  // rules split per circuit (dr-air-flow-pb/zn, dr-froth-height-pb/zn), so
  // each fm id here points at the Pb-circuit row as the representative —
  // the Zn-circuit sibling is cross-referenced in the coverage table
  // (docs/engines/diagnosis.md) and the crosswalk (docs/schema/diagnostic-rule.md)
  // but is not a second value in this map.
  'fm-media-size': 'dr-media-size',
  'fm-grate-wear': 'dr-grate-wear',
  'fm-belt-weigher': 'dr-belt-weigher',
  'fm-air-flow': 'dr-air-flow-pb',
  'fm-froth-height': 'dr-froth-height-pb',
};
